import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Receipt } from 'lucide-react';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { useAdminAgentDetail } from '../../../hooks/useApi';
import { usePermission } from '../../../hooks/useAuth';
import { AgentPaymentTransactionsPanel } from './AgentPaymentTransactionsPanel';
import { PageHeader } from '@kioskgaming/ui';

const AgentPaymentTransactionsPage: React.FC = () => {
  const { id: agentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canManage = usePermission('agentManagement');
  const { data: detailData, isLoading: detailLoading, error: detailError } = useAdminAgentDetail(
    agentId,
    canManage,
  );
  const agent = detailData?.data?.agent;

  if (!agentId) {
    return <div className="p-6 text-muted-foreground">Invalid agent.</div>;
  }

  if (!canManage) {
    return (
      <div className="p-6">
        <p className="text-destructive">You do not have permission to view this page.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/agents')}>
          Back to agents
        </Button>
      </div>
    );
  }

  if (detailLoading && !agent) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (detailError || !agent) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/agents')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to agents
        </Button>
        <p className="text-destructive">Agent not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        back={{ onClick: () => navigate(`/agents/${agentId}`), label: 'Back' }}
        title="Payment transactions"
        titleAddon={<Receipt className="h-7 w-7 text-primary" aria-hidden />}
        description={
          <>
            Agent: <span className="font-medium text-foreground">{agent.name}</span> · {agent.email}
          </>
        }
      />

      <AgentPaymentTransactionsPanel agentId={agentId} showAdminTransactionsLink />
    </div>
  );
};

export default AgentPaymentTransactionsPage;
